import {
  Alert,
  Badge,
  Button,
  EmptyState,
  KeyValue,
  MoreMenu,
  PageHeader,
  Section,
  Skeleton,
  Text,
  useToast,
  type MenuItem,
} from '@ui';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { BackLink } from '../../../app/BackLink';
import { ConfirmDialog } from '../../../app/ConfirmDialog';
import { errorMessage } from '../../../lib/errors';
import { formatDate, formatDateTime } from '../../../lib/format';
import { useSession } from '../../identity/session';
import { formatEuro } from '../../sales/format';
import { receiptUrl, useDeleteEntry, useEntry, useReceipt, type Entry } from '../api';
import { EntryFormDialog } from '../components/EntryFormDialog';
import styles from './FinancePages.module.css';

/** An income or expense with its receipt (FIN-002, FIN-004, FIN-007). */
export function EntryDetailPage() {
  const { t } = useTranslation();
  const { entryId = '' } = useParams();
  const entry = useEntry(entryId);
  if (entry.error) {
    return (
      <EmptyState
        size="page"
        level={1}
        title={errorMessage(entry.error)}
        actions={<BackLink to="/finanzen/buchungen" label={t('finance.tabs.entries')} />}
      />
    );
  }
  if (!entry.data) {
    return (
      <div role="status" aria-label={t('common.loading')} className={styles.page}>
        <Skeleton width="40%" height="2rem" />
        <Skeleton lines={6} />
      </div>
    );
  }
  return <EntryDetail entry={entry.data} />;
}

function EntryDetail({ entry }: { entry: Entry }) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { can } = useSession();
  const [open, setOpen] = useState<'edit' | 'delete' | 'removeReceipt' | null>(null);
  const remove = useDeleteEntry();
  const receipt = useReceipt();
  const file = useRef<HTMLInputElement>(null);
  const write = can('finance.write');
  const manual = entry.source.kind === 'manual';
  const url = entry.receipt ? receiptUrl(entry.id, entry.version) : null;

  const removeReceipt = () =>
    receipt.mutate(
      { id: entry.id, file: null },
      {
        onSuccess: () => {
          setOpen(null);
          toast.show(t('finance.receipt.removed'));
        },
      },
    );

  // Only manual entries are edited here; automatic ones change with their sale (FIN-007).
  const more: MenuItem[] = write
    ? [
        ...(manual
          ? [{ key: 'edit', label: t('common.edit'), onSelect: () => setOpen('edit') }]
          : []),
        ...(entry.receipt
          ? [
              {
                key: 'replace',
                label: t('finance.receipt.replace'),
                onSelect: () => file.current?.click(),
              },
              {
                key: 'remove',
                label: t('finance.receipt.remove'),
                onSelect: () => setOpen('removeReceipt'),
              },
            ]
          : []),
        ...(manual
          ? [
              {
                key: 'delete',
                label: t('common.delete'),
                tone: 'danger' as const,
                separated: true,
                onSelect: () => setOpen('delete'),
              },
            ]
          : []),
      ]
    : [];

  return (
    <div className={styles.page}>
      <input
        ref={file}
        type="file"
        accept="image/*,application/pdf"
        hidden
        aria-label={t('finance.receipt.choose')}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          receipt.mutate(
            { id: entry.id, file: f },
            {
              onSuccess: () => toast.show(t('finance.receipt.saved')),
              onError: (err) => toast.show(errorMessage(err), 'danger'),
            },
          );
        }}
      />
      <PageHeader
        back={<BackLink to="/finanzen/buchungen" label={t('finance.tabs.entries')} />}
        title={`${entry.kind === 'income' ? '+' : '−'}${formatEuro(entry.amount_cents)}`}
        meta={
          <>
            {[
              t(`finance.kind.${entry.kind}`),
              formatDate(entry.booked_on),
              entry.category.name,
              manual ? null : t(`finance.source.${entry.source.kind}`),
            ]
              .filter(Boolean)
              .join(' · ')}
            {entry.cancelled && (
              <>
                {' · '}
                <Badge size="sm">{t('finance.cancelled')}</Badge>
              </>
            )}
          </>
        }
        actions={more.length > 0 && <MoreMenu label={t('common.moreActions')} items={more} />}
      />
      {!manual && (
        <Alert tone="info" title={t('finance.detail.automaticTitle')}>
          {entry.cancelled ? t('finance.detail.cancelledText') : t('finance.detail.automaticText')}{' '}
          {entry.source.kind === 'sale' && entry.source.id && (
            <Link to={`/verkauf/${entry.source.id}`}>{t('finance.detail.openSale')}</Link>
          )}
          {entry.source.kind === 'market_day' && entry.source.id && (
            <Link to={`/verkauf/markttage/${entry.source.id}`}>
              {t('finance.detail.openMarketDay', { name: entry.source.name })}
            </Link>
          )}
        </Alert>
      )}
      <div className={styles.detail}>
        <Section
          title={t('finance.detail.details')}
          footer={
            entry.changed.at &&
            (entry.changed.by
              ? t('apiary.colony.changed', {
                  date: formatDateTime(entry.changed.at),
                  name: entry.changed.by,
                })
              : t('apiary.colony.changedNoName', { date: formatDateTime(entry.changed.at) }))
          }
        >
          <KeyValue
            items={[
              {
                key: 'description',
                label: t('finance.form.description'),
                value: entry.description,
              },
              {
                key: 'counterparty',
                label: t(`finance.form.counterparty.${entry.kind}`),
                value: entry.counterparty,
              },
              {
                key: 'method',
                label: t('finance.form.method'),
                value: entry.method && t(`finance.method.${entry.method}`),
              },
              {
                key: 'notes',
                label: t('finance.form.notes'),
                value: entry.notes && <span className={styles.notes}>{entry.notes}</span>,
              },
            ]}
          />
        </Section>
        <Section title={t('finance.receipt.title')} variant="content">
          {url && entry.receipt ? (
            entry.receipt.content_type === 'application/pdf' ? (
              <iframe
                className={styles.receiptFrame}
                src={`${url}#navpanes=0&view=FitH`}
                title={entry.receipt.filename}
              />
            ) : (
              <a href={url} target="_blank" rel="noreferrer">
                <img className={styles.receipt} src={url} alt={entry.receipt.filename} />
              </a>
            )
          ) : write ? (
            <div>
              <Button
                variant="plain"
                loading={receipt.isPending}
                onClick={() => file.current?.click()}
              >
                {t('finance.receipt.attach')}
              </Button>
            </div>
          ) : (
            <Text tone="muted">{t('finance.receipt.none')}</Text>
          )}
        </Section>
      </div>
      {open === 'edit' && (
        <EntryFormDialog open kind={entry.kind} entry={entry} onClose={() => setOpen(null)} />
      )}
      {/* The file is deleted, so this needs a confirmation (docs/ux-richtlinien.md §6.10). */}
      <ConfirmDialog
        open={open === 'removeReceipt'}
        onClose={() => {
          receipt.reset();
          setOpen(null);
        }}
        title={t('finance.receipt.removeTitle')}
        confirmLabel={t('finance.receipt.remove')}
        pending={receipt.isPending}
        error={receipt.error ? errorMessage(receipt.error) : undefined}
        onConfirm={removeReceipt}
      />
      <ConfirmDialog
        open={open === 'delete'}
        onClose={() => {
          remove.reset();
          setOpen(null);
        }}
        title={t('finance.detail.deleteTitle')}
        description={t('finance.detail.deleteText')}
        confirmLabel={t('common.delete')}
        pending={remove.isPending}
        error={remove.error ? errorMessage(remove.error) : undefined}
        onConfirm={() =>
          remove.mutate(entry.id, {
            onSuccess: () => {
              toast.show(t('finance.detail.deleted'));
              void navigate('/finanzen/buchungen');
            },
          })
        }
      />
    </div>
  );
}
